export interface TemplateInfo {
  id: string;
  name: string;
  family: 'standard' | 'split' | 'sidebar' | 'landing' | 'magazine';
  description: string;
  tier: 'free' | 'premium';
  colors: { primary: string; secondary: string; background: string; text: string };
  fonts: { heading: string; body: string };
  demoUrl: string;
  demoBusiness: string;
  features?: string[];
}

/** Human-readable labels for layout families */
export const familyLabels: Record<TemplateInfo['family'], string> = {
  standard: 'Standard Layout',
  split: 'Split Layout',
  sidebar: 'Sidebar Layout',
  landing: 'Landing Page Layout',
  magazine: 'Magazine Layout',
};

/** Short descriptions for layout families */
export const familyDescriptions: Record<TemplateInfo['family'], string> = {
  standard: 'Classic top navigation with full-width banner',
  split: 'Split-screen banner with phone number in navigation',
  sidebar: 'Sticky side navigation with split-screen banner',
  landing: 'Single-page scroll with anchor navigation',
  magazine: 'Content-first editorial with featured posts grid',
};

// V1 templates have been removed. This array is kept for backward compatibility
// with any legacy V1 sites still in the database.
export const templates: TemplateInfo[] = [];

// ─── V2 Types & Data ─────────────────────────────────────────────────

export interface V2Layout {
  id: string;
  templateId: string;
  name: string;
  description: string;
  icon: string;
}

export interface V2Starter {
  id: string;
  name: string;
  description: string;
  layout: string;
  thumbnail: string;
  variables: Record<string, unknown>;
}

export const v2Layouts: V2Layout[] = [
  { id: 'standard', templateId: 'v2-standard', name: 'Standard', description: 'Classic top navigation with full-width banner. The most versatile layout.', icon: 'standard' },
  { id: 'split', templateId: 'v2-split', name: 'Split', description: 'Split-screen banner with phone number in navigation. Great for services.', icon: 'split' },
  { id: 'sidebar', templateId: 'v2-sidebar', name: 'Sidebar', description: 'Sticky side navigation with compact header. Perfect for portfolios.', icon: 'sidebar' },
  { id: 'landing', templateId: 'v2-landing', name: 'Landing Page', description: 'Everything on one page with smooth scroll. Ideal for personal brands.', icon: 'landing' },
];

export const v2Starters: V2Starter[] = [
  {
    id: 'coastal',
    name: 'Coastal',
    description: 'Clean and airy with ocean tones. Perfect for artisan, beach, and lifestyle brands.',
    layout: 'standard',
    thumbnail: 'coastal.jpg',
    variables: {
      heroStyle: 'fullwidth',
      colorPrimary: '#1a3a4a',
      colorPrimaryLight: '#2d6a7e',
      colorSecondary: '#5ba3b5',
      colorAccent: '#e07a5f',
      colorBackground: '#ffffff',
      colorBackgroundAlt: '#faf8f4',
      colorSurface: '#ffffff',
      colorText: '#2c2c2c',
      colorTextMuted: '#4a4a4a',
      colorHeading: '#1a3a4a',
      colorBorder: '#d4c9b5',
      colorFooterBg: '#1a3a4a',
      fontHeading: 'DM Serif Display',
      fontBody: 'Inter',
      fontWeightHeading: '400',
      borderRadius: '8px',
      borderRadiusLg: '12px',
      cardRadius: '12px',
      buttonRadius: '8px',
      imageRadius: '12px',
      cardShadow: '0 1px 3px rgba(0,0,0,0.06)',
      sectionSpacing: '6rem',
      containerWidth: '1200px',
      homeLayout: 'about-split',
      sectionDivider: 'wave',
      navStyle: 'transparent',
      footerCurve: false,
    },
  },
  {
    id: 'bold',
    name: 'Bold',
    description: 'High contrast with sharp edges and strong typography. For brands that demand attention.',
    layout: 'standard',
    thumbnail: 'bold.jpg',
    variables: {
      heroStyle: 'fullwidth',
      colorPrimary: '#1a1a1a',
      colorPrimaryLight: '#333333',
      colorSecondary: '#ff4444',
      colorAccent: '#ff4444',
      colorBackground: '#ffffff',
      colorBackgroundAlt: '#f5f5f5',
      colorSurface: '#ffffff',
      colorText: '#1a1a1a',
      colorTextMuted: '#555555',
      colorHeading: '#1a1a1a',
      colorBorder: '#e0e0e0',
      colorFooterBg: '#1a1a1a',
      fontHeading: 'Bebas Neue',
      fontBody: 'Inter',
      fontWeightHeading: '400',
      headingLetterSpacing: '0.05em',
      headingTextTransform: 'uppercase',
      borderRadius: '0px',
      borderRadiusSm: '0px',
      borderRadiusLg: '0px',
      cardRadius: '0px',
      buttonRadius: '0px',
      imageRadius: '0px',
      cardShadow: 'none',
      sectionSpacing: '4rem',
      containerWidth: '1200px',
      headingRuleHeight: '4px',
      headingRuleColor: '#ff4444',
      homeLayout: 'about-split',
      navStyle: 'solid',
      footerCurve: false,
    },
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Clean whitespace and understated elegance. Let your content breathe.',
    layout: 'standard',
    thumbnail: 'minimal.jpg',
    variables: {
      heroStyle: 'text-only',
      colorPrimary: '#1a1a1a',
      colorPrimaryLight: '#444444',
      colorSecondary: '#888888',
      colorAccent: '#888888',
      colorBackground: '#fafafa',
      colorBackgroundAlt: '#f0f0f0',
      colorSurface: '#ffffff',
      colorText: '#333333',
      colorTextMuted: '#777777',
      colorHeading: '#1a1a1a',
      colorBorder: '#e8e8e8',
      colorFooterBg: '#1a1a1a',
      fontHeading: 'DM Sans',
      fontBody: 'DM Sans',
      fontWeightHeading: '500',
      borderRadius: '4px',
      borderRadiusSm: '2px',
      borderRadiusLg: '6px',
      cardRadius: '6px',
      buttonRadius: '4px',
      imageRadius: '6px',
      cardShadow: '0 1px 2px rgba(0,0,0,0.04)',
      sectionSpacing: '6rem',
      containerWidth: '1000px',
      headingRuleWidth: '0',
      cardHoverLift: '0px',
      navStyle: 'transparent',
      footerCurve: false,
    },
  },
  {
    id: 'earthy',
    name: 'Earthy',
    description: 'Warm and organic with natural tones. For cafes, farms, wellness, and handmade goods.',
    layout: 'standard',
    thumbnail: 'earthy.jpg',
    variables: {
      heroStyle: 'gradient',
      colorPrimary: '#3d2b1f',
      colorPrimaryLight: '#5c4033',
      colorSecondary: '#7a9e7e',
      colorAccent: '#d4a574',
      colorBackground: '#faf6f1',
      colorBackgroundAlt: '#f0ebe4',
      colorSurface: '#ffffff',
      colorText: '#3d2b1f',
      colorTextMuted: '#6b5b4e',
      colorHeading: '#3d2b1f',
      colorBorder: '#d9cfc4',
      colorFooterBg: '#2a1f15',
      fontHeading: 'Cormorant Garamond',
      fontBody: 'Lato',
      fontWeightHeading: '700',
      borderRadius: '8px',
      borderRadiusLg: '16px',
      cardRadius: '16px',
      buttonRadius: '8px',
      imageRadius: '16px',
      cardShadow: '0 2px 8px rgba(0,0,0,0.06)',
      sectionSpacing: '5rem',
      containerWidth: '1100px',
      heroGradientStart: '#3d2b1f',
      homeLayout: 'about-split',
      sectionDivider: 'curve',
      navStyle: 'transparent',
      footerCurve: true,
    },
  },
  {
    id: 'shaper',
    name: 'Shaper',
    description: 'Premium and refined with navy and gold accents. For luxury brands and professional services.',
    layout: 'standard',
    thumbnail: 'shaper.jpg',
    variables: {
      heroStyle: 'fullwidth',
      colorPrimary: '#1b2a4a',
      colorPrimaryLight: '#2d4270',
      colorSecondary: '#c9a84c',
      colorAccent: '#c9a84c',
      colorBackground: '#ffffff',
      colorBackgroundAlt: '#f5f4f0',
      colorSurface: '#ffffff',
      colorText: '#2c2c2c',
      colorTextMuted: '#5a5a5a',
      colorHeading: '#1b2a4a',
      colorBorder: '#ddd8cd',
      colorFooterBg: '#1b2a4a',
      fontHeading: 'Libre Baskerville',
      fontBody: 'DM Sans',
      fontWeightHeading: '700',
      borderRadius: '6px',
      borderRadiusLg: '10px',
      cardRadius: '10px',
      buttonRadius: '6px',
      imageRadius: '10px',
      cardShadow: '0 2px 8px rgba(0,0,0,0.06)',
      sectionSpacing: '5rem',
      containerWidth: '1100px',
      homeLayout: 'about-split',
      navStyle: 'transparent',
      footerCurve: false,
    },
  },
  {
    id: 'trades',
    name: 'Trades',
    description: 'Light blue and charcoal with bold type. Built for tradespeople, contractors, and service businesses.',
    layout: 'split',
    thumbnail: 'trades.jpg',
    variables: {
      heroStyle: 'split',
      colorPrimary: '#1c2b3a',
      colorPrimaryLight: '#2a4a6b',
      colorSecondary: '#3b82f6',
      colorAccent: '#f59e0b',
      colorBackground: '#ffffff',
      colorBackgroundAlt: '#f1f5f9',
      colorSurface: '#ffffff',
      colorText: '#334155',
      colorTextMuted: '#64748b',
      colorHeading: '#1c2b3a',
      colorBorder: '#e2e8f0',
      colorFooterBg: '#1c2b3a',
      fontHeading: 'Barlow',
      fontBody: 'Inter',
      fontWeightHeading: '700',
      headingLetterSpacing: '0em',
      headingTextTransform: 'none',
      borderRadius: '8px',
      borderRadiusSm: '4px',
      borderRadiusLg: '12px',
      cardRadius: '12px',
      buttonRadius: '8px',
      imageRadius: '12px',
      cardShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
      sectionSpacing: '5rem',
      containerWidth: '1200px',
      footerCurve: false,
    },
  },
  {
    id: 'harvest',
    name: 'Harvest',
    description: 'Warm olive and cream with rustic serif headings. For farms, markets, artisan food, and rural businesses.',
    layout: 'split',
    thumbnail: 'harvest.jpg',
    variables: {
      heroStyle: 'split',
      colorPrimary: '#4a5d23',
      colorPrimaryLight: '#6b7f3a',
      colorSecondary: '#8b6914',
      colorAccent: '#c2410c',
      colorBackground: '#fdfbf7',
      colorBackgroundAlt: '#f5f0e6',
      colorSurface: '#ffffff',
      colorText: '#3d3d3d',
      colorTextMuted: '#6b6b6b',
      colorHeading: '#3a4a1e',
      colorBorder: '#e0d8c8',
      colorFooterBg: '#3a4a1e',
      fontHeading: 'Fraunces',
      fontBody: 'Inter',
      fontWeightHeading: '700',
      headingLetterSpacing: '-0.01em',
      headingTextTransform: 'none',
      borderRadius: '6px',
      borderRadiusSm: '4px',
      borderRadiusLg: '10px',
      cardRadius: '10px',
      buttonRadius: '6px',
      imageRadius: '10px',
      cardShadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
      sectionSpacing: '5rem',
      containerWidth: '1200px',
      sectionDivider: 'curve',
      footerCurve: false,
    },
  },
  {
    id: 'agency',
    name: 'Agency',
    description: 'Minimal white and black with a clean accent. Modern, sharp, and confident — for agencies, studios, and consultancies.',
    layout: 'split',
    thumbnail: 'agency.jpg',
    variables: {
      heroStyle: 'split',
      colorPrimary: '#0a0a0a',
      colorPrimaryLight: '#333333',
      colorSecondary: '#6366f1',
      colorAccent: '#6366f1',
      colorBackground: '#ffffff',
      colorBackgroundAlt: '#fafafa',
      colorSurface: '#ffffff',
      colorText: '#27272a',
      colorTextMuted: '#71717a',
      colorHeading: '#0a0a0a',
      colorBorder: '#e4e4e7',
      colorFooterBg: '#0a0a0a',
      fontHeading: 'Space Grotesk',
      fontBody: 'Inter',
      fontWeightHeading: '700',
      headingLetterSpacing: '-0.02em',
      headingTextTransform: 'none',
      borderRadius: '6px',
      borderRadiusSm: '4px',
      borderRadiusLg: '8px',
      cardRadius: '8px',
      buttonRadius: '6px',
      imageRadius: '8px',
      cardShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
      sectionSpacing: '5rem',
      containerWidth: '1200px',
      footerCurve: false,
    },
  },
  {
    id: 'studio',
    name: 'Studio',
    description: 'Clean creative with white and black. DM Serif Display headings, masonry gallery, and refined minimalism.',
    layout: 'sidebar',
    thumbnail: 'studio.jpg',
    variables: {
      colorPrimary: '#1a1a1a',
      colorPrimaryLight: '#333333',
      colorSecondary: '#c9a96e',
      colorAccent: '#c9a96e',
      colorBackground: '#ffffff',
      colorBackgroundAlt: '#fafafa',
      colorSurface: '#ffffff',
      colorText: '#333333',
      colorTextMuted: '#666666',
      colorHeading: '#1a1a1a',
      colorBorder: '#e8e8e8',
      colorFooterBg: '#fafafa',
      fontHeading: 'DM Serif Display',
      fontBody: 'Inter',
      fontWeightHeading: '400',
      headingLetterSpacing: '-0.01em',
      headingTextTransform: 'none',
      borderRadius: '4px',
      borderRadiusSm: '2px',
      borderRadiusLg: '8px',
      cardRadius: '4px',
      buttonRadius: '2px',
      imageRadius: '4px',
      cardShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
      sectionSpacing: '5rem',
      containerWidth: '960px',
      sidebarWidth: '260px',
      sidebarBg: '#1a1a1a',
      sidebarText: '#ffffff',
      sidebarLink: 'rgba(255, 255, 255, 0.65)',
      sidebarLinkHover: '#ffffff',
      headingRuleWidth: '0',
      galleryStyle: 'masonry',
    },
  },
  {
    id: 'darkroom',
    name: 'Darkroom',
    description: 'Dark mode photography feel with moody tones. Space Mono headings, high contrast, and dramatic presence.',
    layout: 'sidebar',
    thumbnail: 'darkroom.jpg',
    variables: {
      colorPrimary: '#e8e8e8',
      colorPrimaryLight: '#cccccc',
      colorSecondary: '#ff6b35',
      colorAccent: '#ff6b35',
      colorBackground: '#111111',
      colorBackgroundAlt: '#1a1a1a',
      colorSurface: '#1e1e1e',
      colorText: '#b0b0b0',
      colorTextMuted: '#777777',
      colorHeading: '#e8e8e8',
      colorBorder: '#2a2a2a',
      colorFooterBg: '#0a0a0a',
      fontHeading: 'Space Mono',
      fontBody: 'Inter',
      fontWeightHeading: '700',
      headingLetterSpacing: '-0.02em',
      headingTextTransform: 'none',
      borderRadius: '0px',
      borderRadiusSm: '0px',
      borderRadiusLg: '0px',
      cardRadius: '0px',
      buttonRadius: '0px',
      imageRadius: '0px',
      cardShadow: 'none',
      sectionSpacing: '5rem',
      containerWidth: '1000px',
      sidebarWidth: '240px',
      sidebarBg: '#0a0a0a',
      sidebarText: '#e8e8e8',
      sidebarLink: 'rgba(232, 232, 232, 0.5)',
      sidebarLinkHover: '#e8e8e8',
      headingRuleWidth: '0',
      cardHoverLift: '0px',
      galleryStyle: 'grid',
    },
  },
  {
    id: 'slate',
    name: 'Slate',
    description: 'Professional muted tones with slate gray and teal accents. Outfit headings with Inter body text for a polished look.',
    layout: 'sidebar',
    thumbnail: 'slate.jpg',
    variables: {
      colorPrimary: '#2d3748',
      colorPrimaryLight: '#4a5568',
      colorSecondary: '#319795',
      colorAccent: '#319795',
      colorBackground: '#f7fafc',
      colorBackgroundAlt: '#edf2f7',
      colorSurface: '#ffffff',
      colorText: '#4a5568',
      colorTextMuted: '#718096',
      colorHeading: '#1a202c',
      colorBorder: '#e2e8f0',
      colorFooterBg: '#edf2f7',
      fontHeading: 'Outfit',
      fontBody: 'Inter',
      fontWeightHeading: '600',
      headingLetterSpacing: '-0.01em',
      headingTextTransform: 'none',
      borderRadius: '8px',
      borderRadiusSm: '4px',
      borderRadiusLg: '12px',
      cardRadius: '8px',
      buttonRadius: '6px',
      imageRadius: '8px',
      cardShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
      sectionSpacing: '4rem',
      containerWidth: '1000px',
      sidebarWidth: '260px',
      sidebarBg: '#2d3748',
      sidebarText: '#ffffff',
      sidebarLink: 'rgba(255, 255, 255, 0.6)',
      sidebarLinkHover: '#ffffff',
      galleryStyle: 'grid',
    },
  },
  {
    id: 'onepage',
    name: 'One Page',
    description: 'Clean and minimal single-page layout. White background with a subtle accent color. Perfect for portfolios, small businesses, and personal brands.',
    layout: 'landing',
    thumbnail: 'onepage.jpg',
    variables: {
      heroStyle: 'fullwidth',
      colorPrimary: '#1a1a2e',
      colorPrimaryLight: '#2d2d4e',
      colorSecondary: '#4a90d9',
      colorAccent: '#4a90d9',
      colorBackground: '#ffffff',
      colorBackgroundAlt: '#f8f9fb',
      colorSurface: '#ffffff',
      colorText: '#333333',
      colorTextMuted: '#666666',
      colorHeading: '#1a1a2e',
      colorBorder: '#e5e7eb',
      colorFooterBg: '#1a1a2e',
      fontHeading: 'Inter',
      fontBody: 'Inter',
      fontWeightHeading: '700',
      borderRadius: '8px',
      borderRadiusSm: '4px',
      borderRadiusLg: '12px',
      cardRadius: '12px',
      buttonRadius: '8px',
      imageRadius: '12px',
      cardShadow: '0 1px 3px rgba(0,0,0,0.06)',
      sectionSpacing: '5rem',
      containerWidth: '1100px',
      navStyle: 'transparent',
      footerCurve: false,
    },
  },
  {
    id: 'summit',
    name: 'Summit',
    description: 'Bold and dramatic single-page layout. Dark background with vibrant accent colors and gradient sections. For events, launches, and impactful brands.',
    layout: 'landing',
    thumbnail: 'summit.jpg',
    variables: {
      heroStyle: 'gradient',
      colorPrimary: '#0f172a',
      colorPrimaryLight: '#1e293b',
      colorSecondary: '#38bdf8',
      colorAccent: '#f472b6',
      colorBackground: '#0f172a',
      colorBackgroundAlt: '#1e293b',
      colorSurface: '#1e293b',
      colorText: '#cbd5e1',
      colorTextMuted: '#94a3b8',
      colorHeading: '#f1f5f9',
      colorBorder: '#334155',
      colorFooterBg: '#020617',
      fontHeading: 'Sora',
      fontBody: 'Inter',
      fontWeightHeading: '700',
      headingLetterSpacing: '-0.02em',
      borderRadius: '12px',
      borderRadiusSm: '6px',
      borderRadiusLg: '16px',
      cardRadius: '16px',
      buttonRadius: '12px',
      imageRadius: '16px',
      cardShadow: '0 2px 8px rgba(0,0,0,0.3)',
      sectionSpacing: '5rem',
      containerWidth: '1200px',
      heroGradientStart: '#0f172a',
      navStyle: 'transparent',
      footerCurve: false,
    },
  },
  {
    id: 'bistro',
    name: 'Bistro',
    description: 'Warm and welcoming for restaurants, cafes, and food businesses. Terracotta tones with Fraunces serif headings.',
    layout: 'landing',
    thumbnail: 'bistro.jpg',
    variables: {
      heroStyle: 'fullwidth',
      colorPrimary: '#6b3a2a',
      colorPrimaryLight: '#8a4f3a',
      colorSecondary: '#c47b5a',
      colorAccent: '#e09c45',
      colorBackground: '#fdf8f2',
      colorBackgroundAlt: '#f3ebe0',
      colorSurface: '#ffffff',
      colorText: '#3a2820',
      colorTextMuted: '#7a6050',
      colorHeading: '#3a2820',
      colorBorder: '#e8d8c8',
      colorFooterBg: '#2a1810',
      fontHeading: 'Fraunces',
      fontBody: 'Lato',
      fontWeightHeading: '700',
      borderRadius: '6px',
      borderRadiusSm: '4px',
      borderRadiusLg: '10px',
      cardRadius: '10px',
      buttonRadius: '6px',
      imageRadius: '10px',
      cardShadow: '0 1px 4px rgba(0,0,0,0.06)',
      sectionSpacing: '5rem',
      containerWidth: '1100px',
      sectionDivider: 'curve',
      navStyle: 'transparent',
      footerCurve: true,
    },
  },
  {
    id: 'folio',
    name: 'Folio',
    description: 'Editorial and refined for freelancers, illustrators, and creative professionals.',
    layout: 'landing',
    thumbnail: 'folio.jpg',
    variables: {
      heroStyle: 'compact',
      colorPrimary: '#1a1a1a',
      colorPrimaryLight: '#333333',
      colorSecondary: '#8b6e4e',
      colorAccent: '#8b6e4e',
      colorBackground: '#f9f7f4',
      colorBackgroundAlt: '#f0ece6',
      colorSurface: '#ffffff',
      colorText: '#2c2c2c',
      colorTextMuted: '#6b6560',
      colorHeading: '#1a1a1a',
      colorBorder: '#e0d8d0',
      colorFooterBg: '#1a1a1a',
      fontHeading: 'Libre Baskerville',
      fontBody: 'Inter',
      fontWeightHeading: '700',
      headingLetterSpacing: '-0.01em',
      borderRadius: '4px',
      borderRadiusSm: '2px',
      borderRadiusLg: '6px',
      cardRadius: '4px',
      buttonRadius: '2px',
      imageRadius: '6px',
      cardShadow: '0 1px 3px rgba(0,0,0,0.05)',
      sectionSpacing: '5rem',
      containerWidth: '1000px',
      navStyle: 'transparent',
      footerCurve: false,
    },
  },
  {
    id: 'bloom',
    name: 'Bloom',
    description: 'Soft and romantic for florists, wedding services, boutiques, and event planners.',
    layout: 'split',
    thumbnail: 'bloom.jpg',
    variables: {
      heroStyle: 'split',
      heroPattern: 'rings',
      colorPrimary: '#7a4f5e',
      colorPrimaryLight: '#9a6a7a',
      colorSecondary: '#8fa882',
      colorAccent: '#c9a46e',
      colorBackground: '#fdfaf8',
      colorBackgroundAlt: '#f5ede8',
      colorSurface: '#ffffff',
      colorText: '#3d2d30',
      colorTextMuted: '#8a7070',
      colorHeading: '#4a2a35',
      colorBorder: '#e8d5d5',
      colorFooterBg: '#3a2030',
      fontHeading: 'Cormorant Garamond',
      fontBody: 'Lato',
      fontWeightHeading: '600',
      headingLetterSpacing: '0.01em',
      borderRadius: '8px',
      borderRadiusSm: '4px',
      borderRadiusLg: '12px',
      cardRadius: '12px',
      buttonRadius: '8px',
      imageRadius: '12px',
      cardShadow: '0 1px 4px rgba(0,0,0,0.05)',
      sectionSpacing: '5rem',
      containerWidth: '1200px',
      navStyle: 'transparent',
      footerCurve: false,
    },
  },
];

/** Get starters for a specific layout */
export function getStartersForLayout(layoutId: string): V2Starter[] {
  return v2Starters.filter((s) => s.layout === layoutId);
}

/** Get a V2 layout by its id */
export function getV2Layout(layoutId: string): V2Layout | undefined {
  return v2Layouts.find((l) => l.id === layoutId);
}

/** Get a V2 starter by its id */
export function getV2Starter(starterId: string): V2Starter | undefined {
  return v2Starters.find((s) => s.id === starterId);
}

/** Keys for advanced/derived colour fields that auto-derive from the 3 main colours */
export const ADVANCED_COLOR_KEYS = new Set([
  'colorPrimaryLight', 'colorAccent',
  'colorBackgroundAlt', 'colorSurface',
  'colorTextMuted', 'colorBorder', 'colorFooterBg',
]);

/** Default fallback for unknown template IDs (legacy V1 sites) */
const FALLBACK_TEMPLATE: TemplateInfo = {
  id: 'unknown',
  name: 'Custom',
  family: 'standard',
  description: 'Custom template',
  tier: 'free',
  colors: { primary: '#3b82f6', secondary: '#64748b', background: '#ffffff', text: '#1a1a1a' },
  fonts: { heading: 'Inter', body: 'Inter' },
  demoUrl: '',
  demoBusiness: '',
};

/**
 * Look up a template by id.
 * For V2 templates (v2-standard, v2-split, etc.), returns info derived from the layout.
 * For legacy V1 templates or unknown IDs, returns a sensible fallback.
 */
export function getTemplate(id: string): TemplateInfo {
  // Check V1 templates first (legacy)
  const v1 = templates.find((t) => t.id === id);
  if (v1) return v1;

  // Check V2 layouts
  if (id.startsWith('v2-')) {
    const layout = v2Layouts.find((l) => l.templateId === id);
    if (layout) {
      // Find the first starter for this layout to get representative colors
      const starter = v2Starters.find((s) => s.layout === layout.id);
      return {
        id: layout.templateId,
        name: layout.name,
        family: layout.id as TemplateInfo['family'],
        description: layout.description,
        tier: 'free',
        colors: starter
          ? {
              primary: (starter.variables.colorPrimary as string) || '#3b82f6',
              secondary: (starter.variables.colorSecondary as string) || '#64748b',
              background: (starter.variables.colorBackground as string) || '#ffffff',
              text: (starter.variables.colorText as string) || '#1a1a1a',
            }
          : FALLBACK_TEMPLATE.colors,
        fonts: starter
          ? {
              heading: (starter.variables.fontHeading as string) || 'Inter',
              body: (starter.variables.fontBody as string) || 'Inter',
            }
          : FALLBACK_TEMPLATE.fonts,
        demoUrl: '',
        demoBusiness: '',
      };
    }
  }

  return FALLBACK_TEMPLATE;
}

/**
 * Merge a template manifest into the static template info.
 * The manifest provides authoritative colors, fonts, description, and features.
 * Static data (demoUrl, demoBusiness) is preserved as it's not in the manifest.
 */
export function mergeManifestIntoTemplate(
  templateInfo: TemplateInfo,
  manifest: { description?: string; colors?: TemplateInfo['colors']; fonts?: TemplateInfo['fonts']; features?: string[]; family?: TemplateInfo['family'] },
): TemplateInfo {
  return {
    ...templateInfo,
    description: manifest.description || templateInfo.description,
    colors: manifest.colors || templateInfo.colors,
    fonts: manifest.fonts || templateInfo.fonts,
    features: manifest.features || templateInfo.features,
    family: manifest.family || templateInfo.family,
  };
}
